<template>
  <div class="smart-recruiters-overview">
    <grid-container-fi>
      <div class="smart-recruiters-overview__filters-wrapper">
        <error-message
          ref="errorMessageComponent"
          hide-initially
        >
          {{ $dict.get('online-Shop.Global-Labels.Error-Code-Subline') }}
        </error-message>

        <div class="smart-recruiters-overview__filters">
          <div class="smart-recruiters-overview__filter">
            <text-input-fi
              :ref="(ref) => (queryRef = ref)"
              v-model="query"
              :placeholder="$dict.get('smart-Recruiters.Free-Search')"
              large-from="lg"
              icon="search"
              hide-details
              class="data-form__fullwidth v-input--large"
              @icon-clicked="queryRef.focus()"
            />
          </div>
          <div class="smart-recruiters-overview__filter">
            <smart-recruiters-filter
              v-model="activeFacets[jobLocationId]"
              :name="$dict.get('smart-Recruiters.Filter-Location')"
              :options="availableJobLocations"
              icon="map-pin"
              @change="selectJobLocation"
            />
          </div>
          <div class="smart-recruiters-overview__filter">
            <smart-recruiters-filter
              v-model="activeFacets[entryTypeId]"
              :name="$dict.get('smart-Recruiters.Filter-Entry-Types')"
              :options="availableEntryTypes"
              @change="selectEntryType"
            />
          </div>
          <div class="smart-recruiters-overview__filter">
            <smart-recruiters-filter
              v-model="activeFacets[fieldOfActivityId]"
              :name="$dict.get('smart-Recruiters.Filter-Field-Of-Activity')"
              :options="availableFieldsOfActivity"
              @change="selectFieldOfActivity"
            />
          </div>
          <div
            v-if="availableDepartments.length > 1"
            class="smart-recruiters-overview__filter"
          >
            <smart-recruiters-filter
              v-model="activeFacets[departmentId]"
              :name="$dict.get('smart-Recruiters.Filter-Department')"
              :options="availableDepartments"
              @change="selectDepartment"
            />
          </div>
        </div>

        <div class="smart-recruiters-overview__separator" />
      </div>
    </grid-container-fi>
    <grid-container-fi>
      <div class="smart-recruiters-overview__sub-filter">
        <div class="smart-recruiters-overview__active-facets">
          <div
            v-if="allSelectedFacets.length"
            class="smart-recruiters-overview__show-active-facets"
            @click="showActiveFilters = !showActiveFilters"
          >
            {{ $dict.get('smart-Recruiters.Show-Filters') }}
            <icon-fi
              v-if="showActiveFilters"
              icon="chevron-up"
            />
            <icon-fi
              v-else
              icon="chevron-down"
            />
          </div>
          <transition
            enter-active-class="animated fadeIn fastest"
            leave-active-class="animated fadeOut fastest"
          >
            <div
              v-if="allSelectedFacets.length || query"
              class="smart-recruiters-overview__selection-wrapper"
            >
              <transition-group
                :class="{
                  'smart-recruiters-overview__selection--expanded': showActiveFilters,
                  'smart-recruiters-overview__selection': true,
                }"
                name="smart-recruiters-overview__filter-list"
                tag="div"
              >
                <smart-recruiters-selected-filter
                  v-if="query"
                  :key="queryId"
                  :facet="{
                    fieldId: queryId,
                    fieldLabel: $dict.get('smart-Recruiters.Free-Search'),
                    label: query,
                  }"
                  @click="query = ''"
                />
                <smart-recruiters-selected-filter
                  v-for="facet in allSelectedFacets"
                  :key="facet.id"
                  :facet="facet"
                  @click="deselectFacet(facet)"
                />
              </transition-group>
              <clear-all-facets-fi
                key="clearAllFilters"
                class="smart-recruiters-overview__clear-all-facets"
                @click="clearAllFilters"
              />
            </div>
          </transition>
        </div>
        <div
          v-if="getSmartRecruitersPostings.totalFound !== undefined"
          class="smart-recruiters-overview__count"
        >
          {{ $dict.get('smart-Recruiters.Results-Count', [filteredContent.length]) }}
        </div>

        <div class="smart-recruiters-overview__big-separator" />
      </div>
    </grid-container-fi>
    <transition
      mode="out-in"
      enter-active-class="animated fadeIn fastest"
      leave-active-class="animated fadeOut fastest"
    >
      <smart-recruiters-postings
        v-if="filteredContent.length"
        class="smart-recruiters-overview__postings"
        :postings="filteredContent"
        :fields="fields"
      />
      <grid-container-fi v-else-if="getSmartRecruitersPostings.content?.length">
        <div class="smart-recruiters-overview__no-results">
          {{ $dict.get('smart-Recruiters.No-Results') }}
        </div>
      </grid-container-fi>
    </transition>
  </div>
</template>

<script setup lang="ts">
import { useQuery } from '@urql/vue';
import { ErrorMessage, IconFi, TextInputFi } from 'atoms';
import { computed, onServerPrefetch, reactive, ref, useTemplateRef, watch } from 'vue';

import ClearAllFacetsFi from '@/components/facets/clear-all-facets-fi/ClearAllFacetsFi.vue';
import GridContainerFi from '@/components/grid-fi/GridContainerFi.vue';
import { useFields } from '@/composables/fields';
import { useDict, useFischer } from '@/composables/fischerPlugin';
import GETSMARTRECRUITERSPOSTINGS from '@/graphql/getSmartRecruitersPostings.gql';

import { departmentId, entryTypeId, fieldOfActivityId } from '../CustomFields';
import SmartRecruitersFilter from './SmartRecruitersFilter.vue';
import SmartRecruitersPostings from './SmartRecruitersPostings.vue';
import SmartRecruitersSelectedFilter from './SmartRecruitersSelectedFilter.vue';

const jobLocationId = 'jobLocation';
const queryId = 'query';

defineOptions({ name: 'SmartRecruitersOverview' });

const fields = useFields('SmartRecruitersOverviewPageFields');

const fischer = useFischer();
const dict = useDict();

const errorMessageRef = useTemplateRef<any>('errorMessageComponent');
const queryRef = ref<any>(null);

const query = ref('');

const activeFacets = reactive<Record<string, any[]>>({
  [entryTypeId]: [],
  [fieldOfActivityId]: [],
  [jobLocationId]: [],
  [departmentId]: [],
});
const activeFacetUrlParamKeys: Record<string, string> = {
  [entryTypeId]: 'entryType',
  [fieldOfActivityId]: 'fieldOfActivity',
  [jobLocationId]: 'jobLocation',
  [departmentId]: 'department',
  [queryId]: 'jobTitle',
};
const filtersInitialized = ref(false);
const activeFacetUrlParams = ref<Record<string, any>>({});
const showActiveFilters = ref(false);

const countryFilter = computed(() => {
  return fields.countryFilter?.value && fields.countryFilter?.value !== ''
    ? fields.countryFilter.value.replace(/\s/g, '').split(',')
    : null;
});

const companyDivisionFilter = computed(() => {
  return fields.companyDivisionFilter?.value && fields.companyDivisionFilter?.value !== ''
    ? fields.companyDivisionFilter.value.replace(/\s/g, '').split(',')
    : null;
});

const variables = computed(() => ({
  language: fischer.siteLanguage(),
  country:
    countryFilter.value?.length === 1 && !fields.countryFilterExcluded?.value
      ? countryFilter.value[0]
      : undefined,
}));

// Default request policy on purpose, and the result is consumed through a computed rather than
// copied into a ref: both are what let the SSR payload reach the hydration render. See
// createSsrExchange.
const { data, error, executeQuery } = useQuery({
  query: GETSMARTRECRUITERSPOSTINGS,
  variables,
});

onServerPrefetch(async () => {
  await executeQuery();
});

const acceptPosting = (posting: any) => {
  let accept = true;

  if (
    (countryFilter.value?.length || 0) > 1 ||
    (countryFilter.value?.length === 1 && fields.countryFilterExcluded?.value)
  ) {
    accept = Boolean(
      fields.countryFilterExcluded?.value
        ? !countryFilter.value?.includes(posting.location.country)
        : countryFilter.value?.includes(posting.location.country),
    );
  }

  if (companyDivisionFilter.value?.length) {
    accept = fields.companyDivisionFilterExcluded?.value
      ? !companyDivisionFilter.value.includes(
          posting.customField.find((field: any) => field.fieldId === departmentId)?.valueId,
        )
      : companyDivisionFilter.value.includes(
          posting.customField.find((field: any) => field.fieldId === departmentId)?.valueId,
        );
  }

  return accept;
};

const postingsResult = computed<any>(() => {
  const content = data.value?.getSmartRecruitersPostings?.content;
  if (!content) return {};
  const filteredPostings = content.filter(acceptPosting);
  return { content: filteredPostings, totalFound: filteredPostings.length };
});

watch(error, (e) => {
  if (e) {
    console.error('Error getting posts', e);
    errorMessageRef.value?.trigger();
  }
});

const getSmartRecruitersPostings = computed(() => postingsResult.value);

const markByJobLocation = (content: any[]) => {
  const activeJobLocations = activeFacets[jobLocationId];
  if (!activeJobLocations.length) return;
  content.forEach((posting) => {
    if (!posting.locations.some((id: string) => activeJobLocations.includes(id))) {
      posting.filterReason[jobLocationId] = 1;
    }
  });
};

const markByQuery = (content: any[]) => {
  if (/\S/.test(query.value)) {
    const terms = query.value.toLocaleLowerCase().trim().split(/\s+/);
    content.forEach((posting) => {
      const name = posting.name.toLocaleLowerCase();
      if (!terms.every((term: string) => name.includes(term))) {
        posting.filterReason[queryId] = 1;
      }
    });
  }
};

const markByCustomField = (content: any[], filterId: string, values: any[]) => {
  if (!values?.length) return;
  content.forEach((posting) => {
    if (!posting.customField) return;
    if (
      !values.some((value) => posting.customField.some((field: any) => field.valueId === value))
    ) {
      posting.filterReason[filterId] = 1;
    }
  });
};

const contentMarkedWithFilterReasons = computed(() => {
  let content = postingsResult.value.content || [];
  content = content.map((item: any) => {
    const mappedItem = { ...item, filterReason: {} };
    const locationId = item.location?.city;
    mappedItem.locations = locationId ? locationId.split(/\s*[,/]\s*/) : [];
    return mappedItem;
  });

  markByJobLocation(content);
  markByQuery(content);
  markByCustomField(content, fieldOfActivityId, activeFacets[fieldOfActivityId]);
  markByCustomField(content, entryTypeId, activeFacets[entryTypeId]);
  markByCustomField(content, departmentId, activeFacets[departmentId]);

  return content;
});

const filteredContent = computed(() =>
  contentMarkedWithFilterReasons.value.filter(
    (item: any) => Object.keys(item.filterReason).length === 0,
  ),
);

const isFieldAvailable = (post: any, fieldId: string) => {
  const filterReasons = Object.keys(post.filterReason);
  return filterReasons.length === 0 || (filterReasons.length === 1 && filterReasons[0] === fieldId);
};

const sortFieldByLabel = (field: any[]) =>
  field.sort((a, b) => {
    if (a.id === b.id) return 0;
    if (a.label === b.label) {
      console.error('Two items have the same label:', a, b);
      return a.id > b.id ? 1 : -1;
    }
    return a.label > b.label ? 1 : -1;
  });

const availableJobLocations = computed(() => {
  const jobLocations: Record<string, boolean> = {};
  contentMarkedWithFilterReasons.value.forEach((post: any) => {
    post.locations.forEach((id: string) => {
      if (isFieldAvailable(post, jobLocationId) || activeFacets[jobLocationId].includes(id)) {
        jobLocations[id] = true;
      }
    });
  });
  const fieldLabel = dict.get('smart-Recruiters.Filter-Location');
  const fieldId = 'location';
  const jobLocationsFields = Object.keys(jobLocations).map((id) => ({
    id,
    value: id,
    label: id,
    fieldLabel,
    fieldId,
    selected: activeFacets[jobLocationId].includes(id),
  }));
  return sortFieldByLabel(jobLocationsFields);
});

const getAvailableCustomFieldValues = (fieldId: string, fieldLabel: string) => {
  const customField: Record<string, any> = {};
  contentMarkedWithFilterReasons.value.forEach((post: any) => {
    const customFields = post.customField;
    const ourField = customFields.find((field: any) => field.fieldId === fieldId);
    if (ourField) {
      if (customField[ourField.valueId]) return;
      if (isFieldAvailable(post, fieldId) || activeFacets[fieldId].includes(ourField.valueId)) {
        customField[ourField.valueId] = ourField;
      }
    }
  });
  const allFields = Object.keys(customField).map((id) => ({
    id,
    label: customField[id].valueLabel,
    fieldId,
    fieldLabel,
    selected: activeFacets[fieldId].includes(id),
  }));
  return sortFieldByLabel(allFields);
};

const availableEntryTypes = computed(() =>
  getAvailableCustomFieldValues(entryTypeId, dict.get('smart-Recruiters.Filter-Entry-Types')),
);
const availableFieldsOfActivity = computed(() =>
  getAvailableCustomFieldValues(
    fieldOfActivityId,
    dict.get('smart-Recruiters.Filter-Field-Of-Activity'),
  ),
);
const availableDepartments = computed(() =>
  getAvailableCustomFieldValues(departmentId, dict.get('smart-Recruiters.Filter-Department')),
);

const allSelectedFacets = computed(() => [
  ...availableJobLocations.value.filter((item: any) => item.selected),
  ...availableEntryTypes.value.filter((item: any) => item.selected),
  ...availableFieldsOfActivity.value.filter((item: any) => item.selected),
  ...availableDepartments.value.filter((item: any) => item.selected),
]);

const allFacets = computed(() => [
  ...availableJobLocations.value,
  ...availableEntryTypes.value,
  ...availableFieldsOfActivity.value,
  ...availableDepartments.value,
]);

const setUrlParams = () => {
  const urlParams = new URLSearchParams();
  Object.entries(activeFacetUrlParams.value).forEach(([key, value]) => {
    if (!value) return;
    urlParams.append(
      activeFacetUrlParamKeys[key],
      key === queryId
        ? value
        : Object.values(value)
            .map((id) => allFacets.value.find((facet: any) => facet.id === id)?.label)
            .join('|'),
    );
  });
  urlParams.sort();
  window.history.pushState(null, '', `?${urlParams}`);
};

const initFilters = () => {
  if (filtersInitialized.value) return;
  Object.entries(activeFacetUrlParamKeys).forEach(([key, value]) => {
    const urlValue = fischer.query(value);
    if (urlValue) {
      if (key === queryId) {
        query.value = urlValue;
      } else {
        const values: any[] = [];
        urlValue.split('|').forEach((label: string) => {
          const id = allFacets.value.find(
            (facet: any) => facet.label === decodeURIComponent(label),
          )?.id;
          if (id) values.push(id);
        });
        activeFacets[key] = values;
      }
    }
  });
  filtersInitialized.value = true;
};

watch(query, (value) => {
  activeFacetUrlParams.value[queryId] = value;
  setUrlParams();
});

watch(
  activeFacets,
  async (value) => {
    activeFacetUrlParams.value = Object.fromEntries(
      Object.entries(value).filter((entry) => (entry[1] as any[])?.length),
    );
    if (query.value) {
      activeFacetUrlParams.value[queryId] = query.value;
    }
    setUrlParams();
  },
  { deep: true },
);

watch(allFacets, () => {
  initFilters();
});

const selectFieldOfActivity = (value: any) => {
  activeFacets[fieldOfActivityId] = value;
};
const selectEntryType = (value: any) => {
  activeFacets[entryTypeId] = value;
};
const selectJobLocation = (value: any) => {
  activeFacets[jobLocationId] = value;
};
const selectDepartment = (value: any) => {
  activeFacets[departmentId] = value;
};

const clearAllFilters = () => {
  query.value = '';
  activeFacets[entryTypeId] = [];
  activeFacets[fieldOfActivityId] = [];
  activeFacets[jobLocationId] = [];
  activeFacets[departmentId] = [];
};

const deselectFacet = ({ fieldId, id }: { fieldId: string; id: string }) => {
  switch (fieldId) {
    case 'location':
      activeFacets[jobLocationId] = activeFacets[jobLocationId].filter((item) => item !== id);
      break;
    case entryTypeId:
    case fieldOfActivityId:
    case departmentId:
      activeFacets[fieldId] = activeFacets[fieldId].filter((item) => item !== id);
      break;
    default:
      console.error('Could not deselect facet (unknown field)', fieldId, id);
  }
};
</script>

<style scoped src="./smart-recruiters-overview" />
